import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import ProjectForm from '../../components/Configuration/ProjectForm';
import { getProjects, deleteProject } from '../../services/projectsService';

const Projects = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const isLoadingRef = useRef(false); // Prevenir llamadas simultáneas
  const [allProjects, setAllProjects] = useState([]);

  // Cargar proyectos desde el backend (solo una vez)
  const loadProjects = async () => {
    // Prevenir llamadas duplicadas simultáneas
    if (isLoadingRef.current) {
      console.log('⏸️ [Projects] Ya hay una carga en progreso, ignorando llamada duplicada');
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      console.log('🔄 [Projects] Iniciando carga de proyectos...');
      
      const response = await getProjects();
      
      console.log('📥 [Projects] Respuesta recibida en componente:');
      console.log('📦 [Projects] response:', response);
      console.log('✅ [Projects] response.success:', response.success);
      console.log('📋 [Projects] response.data:', response.data);
      console.log('🔍 [Projects] Tipo de response.data:', typeof response.data);
      console.log('📊 [Projects] Es array?:', Array.isArray(response.data));
      
      if (response.success) {
        const projectsData = response.data || [];
        console.log('💾 [Projects] Guardando proyectos en estado:');
        console.log('🔢 [Projects] Cantidad de proyectos a guardar:', projectsData.length);
        console.log('📝 [Projects] Primeros 3 proyectos:', projectsData.slice(0, 3));
        
        if (projectsData.length > 0) {
          console.log('🔑 [Projects] Estructura del primer proyecto:', {
            id: projectsData[0].id,
            code: projectsData[0].code,
            name: projectsData[0].name,
            type: projectsData[0].type,
            status: projectsData[0].status,
            is_active: projectsData[0].is_active,
            keys: Object.keys(projectsData[0])
          });
        }
        
        setAllProjects(projectsData);
        console.log('✅ [Projects] Proyectos guardados en estado exitosamente');
      } else {
        console.warn('⚠️ [Projects] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los proyectos',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Projects] Error al cargar proyectos:', error);
      console.error('📊 [Projects] Error.response:', error.response);
      console.error('📋 [Projects] Error.response?.data:', error.response?.data);
      console.error('🔢 [Projects] Error.status:', error.response?.status);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los proyectos',
        type: 'error'
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      console.log('🏁 [Projects] Proceso de carga finalizado');
    }
  };

  useEffect(() => {
    // Evitar doble carga en React StrictMode - Carga inicial única
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      console.log('🚀 [Projects] Primera carga - ejecutando loadProjects()');
      loadProjects();
    } else {
      console.log('⏭️ [Projects] Ya se cargó anteriormente, omitiendo carga duplicada');
    }
  }, []);

  // Filtrar proyectos localmente en el frontend
  const filteredProjects = allProjects.filter(project => {
    const matchesSearch = !searchTerm || 
      project.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Calcular estadísticas
  const totalProjects = filteredProjects.length;
  const activeProjects = filteredProjects.filter(p => p.status === 'active').length;
  const planningProjects = filteredProjects.filter(p => p.status === 'planning').length;
  const completedProjects = filteredProjects.filter(p => p.status === 'completed').length;

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    
    try {
      const response = await deleteProject(projectToDelete.id);
      
      if (response.success) {
        setAllProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
        setToast({
          isOpen: true,
          message: 'Proyecto eliminado exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el proyecto',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar proyecto:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el proyecto',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    // Recargar todos los proyectos desde el backend
    loadProjects();
    setToast({
      isOpen: true,
      message: editingProject ? 'Proyecto actualizado exitosamente' : 'Proyecto creado exitosamente',
      type: 'success'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      planning: 'Planificación',
      active: 'Activo',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      proyecto: 'Proyecto',
      operacion: 'Operación'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      proyecto: 'bg-purple-100 text-purple-800',
      operacion: 'bg-indigo-100 text-indigo-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Proyectos</h1>
              <p className="text-gray-600 mt-1">Gestione los proyectos de la empresa</p>
            </div>
            <button
              onClick={() => {
                setEditingProject(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nuevo Proyecto
            </button>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Proyectos</span>
          </nav>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex gap-2">
              {/* Filtro por Estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Estados</option>
                <option value="planning">Planificación</option>
                <option value="active">Activo</option>
                <option value="completed">Completado</option>
                <option value="cancelled">Cancelado</option>
              </select>

              {/* Búsqueda */}
              <input
                type="text"
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Proyectos</div>
            <div className="text-2xl font-bold text-gray-800">{totalProjects}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Proyectos Activos</div>
            <div className="text-2xl font-bold text-green-600">{activeProjects}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">En Planificación</div>
            <div className="text-2xl font-bold text-yellow-600">{planningProjects}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Completados</div>
            <div className="text-2xl font-bold text-blue-600">{completedProjects}</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Cargando proyectos...
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'No se encontraron proyectos con los filtros aplicados' 
                : 'No hay proyectos registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Inicio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Fin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50">
                      {/* Código */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono font-medium text-teal-600">{project.code}</span>
                      </td>

                      {/* Nombre */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{project.name}</div>
                        {project.description && (
                          <div className="text-xs text-gray-500 mt-1">{project.description}</div>
                        )}
                      </td>

                      {/* Tipo */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(project.type)}`}>
                          {getTypeLabel(project.type)}
                        </span>
                      </td>

                      {/* Fecha Inicio */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{formatDate(project.start_date)}</span>
                      </td>

                      {/* Fecha Fin */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{formatDate(project.end_date)}</span>
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(project.status)}`}>
                          {getStatusLabel(project.status)}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingProject(project);
                            setIsFormOpen(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(project)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Formulario */}
        <ProjectForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingProject(null);
          }}
          project={editingProject}
          onSuccess={handleFormSuccess}
        />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setProjectToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Proyecto"
          message={`¿Estás seguro de que deseas eliminar el proyecto "${projectToDelete?.name}" (${projectToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Projects;

